import React, {useEffect, useMemo, useState} from 'react'
import {useAppSelector} from 'src/hooks'
import apiClient from 'src/services/apiClient'
import AdherenceTabs, {AdherenceFetcher} from 'src/pages-new/analytics/AdherenceTabs'
import {
  AdherenceTherapyType,
  isSecondaryAdherenceEnabled,
  resolveAdherenceFiltersConfig,
  resolveAdherenceLabels,
  resolveAdherenceTabs,
} from 'src/utils/adherenceCharts'

// The logged-in user's own adherence, one endpoint per tab
const FETCHERS: Record<AdherenceTherapyType, AdherenceFetcher> = {
  primary: (filters, setStatus) => apiClient.getTherapiesAdherence(filters, setStatus),
  secondary: (filters, setStatus) => apiClient.getSecondaryTherapiesAdherence(filters, setStatus),
}

// Analytics page of the patient / insurance user: adherence to their primary and secondary
// therapies, i.e. COMPLETED assumptions over every assumption due in the selected range.
export default function AnalyticsNew(): JSX.Element {
  const analyticsConfigurations = useAppSelector(state => state.pspConfigReducer.data.analyticsConfigurations)
  const configuredFilters = analyticsConfigurations?.filtersConfig
  const sectionsConfig = analyticsConfigurations?.sections
  const labels = useMemo(() => resolveAdherenceLabels(analyticsConfigurations?.labels), [analyticsConfigurations?.labels])
  const visibleFilters = useMemo(() => resolveAdherenceFiltersConfig(configuredFilters), [configuredFilters])
  const secondaryEnabled = isSecondaryAdherenceEnabled(sectionsConfig)
  // undefined while still unknown
  const [hasSecondaryTherapies, setHasSecondaryTherapies] = useState<boolean>()

  useEffect(() => {
    // A disabled section is never shown, whatever the patient has
    if (!secondaryEnabled) {
      setHasSecondaryTherapies(false)
      return
    }
    // Same rule as DiaryNew: the "Terapie Accessorie" tab only exists for a patient who has some.
    apiClient.getSecondaryTherapies(() => {})
      .then((therapies: unknown) => setHasSecondaryTherapies(Array.isArray(therapies) && therapies.length > 0))
      .catch(() => setHasSecondaryTherapies(false))
  }, [secondaryEnabled])

  // Only the sections enabled in the configuration (Terapie Accessorie also needs some therapies)
  const tabs = useMemo(() => resolveAdherenceTabs(sectionsConfig, Boolean(hasSecondaryTherapies)), [sectionsConfig, hasSecondaryTherapies])
  // every section disabled (or only Terapie Accessorie, for a patient who has none)
  const noSections = tabs.length === 0 && hasSecondaryTherapies !== undefined

  return (
    <AdherenceTabs
      tabs={tabs}
      fetchers={FETCHERS}
      visibleFilters={visibleFilters}
      labels={labels}
      noSections={noSections}
    />
  )
}
